import { useSyncExternalStore } from 'react';

type Listener = () => void;
class UserInteractedStore {
    private _value = false;
    private listeners = new Set<Listener>();
    private installed = false;

    get value() {
        return this._value;
    }

    subscribe = (cb: Listener) => {
        this.listeners.add(cb);
        return () => this.listeners.delete(cb);
    };

    private emit() {
        for (const l of this.listeners) l();
    }

    markInteracted() {
        if (this._value) return;
        this._value = true;
        try {
            window.dispatchEvent(
                new CustomEvent('tc-user-interacted', { detail: { at: Date.now() } }),
            );
        } catch {/* noop */ }
        this.emit();
    }

    ensureOneShotListeners() {
        if (this.installed || this._value || typeof window === 'undefined') return;
        this.installed = true;
        const handler = () => {
            this.markInteracted();
            cleanup();
        };
        const cleanup = () => {
            window.removeEventListener('click', handler);
            window.removeEventListener('keydown', handler);
            window.removeEventListener('touchstart', handler);
        };
        window.addEventListener('click', handler, { once: true });
        window.addEventListener('keydown', handler, { once: true });
        window.addEventListener('touchstart', handler, { once: true });
    }
}

export const userInteractedStore = new UserInteractedStore();

if (typeof window !== 'undefined') {
    userInteractedStore.ensureOneShotListeners();
}

export function useUserInteracted(): boolean {
    if (typeof window !== 'undefined') {
        userInteractedStore.ensureOneShotListeners();
    }
    return useSyncExternalStore(
        userInteractedStore.subscribe,
        () => userInteractedStore.value,
        () => false,
    );
}

export function waitForUserInteracted(): Promise<void> {
    if (userInteractedStore.value) return Promise.resolve();
    return new Promise(resolve => {
        const off = userInteractedStore.subscribe(() => {
            if (userInteractedStore.value) {
                off();
                resolve();
            }
        });
        userInteractedStore.ensureOneShotListeners();
    });
}
